/**
 * `BrutalistRadioGroup` — the reusable GMS//OS brutalist single-select, styled as a **segmented
 * button-switcher** rather than a stacked form of radio dots. It is the shared form of the ad-hoc
 * "pick one" button rows the combat choosers grew (the firing-profile switcher
 * `.skirmish-hud-prof`, the Quick-Tech option row): a horizontal strip of joined segments where the
 * chosen one inverts to ink-on-paper, matching the rest of the brutalist family
 * (`brutalist-tokens.css`).
 *
 * It wraps **real `<input type="radio">`s** (visually hidden, one `<label>` segment each) rather
 * than `role="radio"` buttons, so it keeps native form semantics for free: arrow-key roving focus
 * within the group, `:checked` state, and `disabled` — the affordance the engine-is-the-wall
 * pattern needs (a server-illegal option renders as a disabled segment, e.g. Patch at 0 repairs).
 *
 * Self-contained: it resolves its palette from the global `--brutalist-*` tokens (so it works on any
 * brutalist surface, not just COMBAT.app) and owns its CSS via the colocated import below.
 */
import { useId, type ReactNode } from "react";
import "./brutalist-radio.css";

export type BrutalistRadioOption<T extends string> = {
  value: T;
  /** Segment face — keep it short (a switcher, not a paragraph); put detail in the group `caption`. */
  label: ReactNode;
  /** Accessible name for the segment when `label` isn't plain text. Defaults to `value`. */
  ariaLabel?: string;
  /** Render as a disabled segment (a mirrored server wall, never a new rule). */
  disabled?: boolean;
  /** Native tooltip — e.g. the reason a segment is disabled. */
  title?: string;
};

export function BrutalistRadioGroup<T extends string>({
  name,
  value,
  options,
  onChange,
  label,
  ariaLabel,
  caption,
}: {
  /**
   * Radio `name` prefix. Each mounted group suffixes it with its own `useId`, so two instances of
   * one chooser on the page at once (the sheet's and the command menu's, which share one aim) stay
   * separate native radio groups instead of unchecking each other.
   */
  name: string;
  value: T;
  options: readonly BrutalistRadioOption<T>[];
  onChange: (value: T) => void;
  /** Section legend above the switcher (mono uppercase). Omit for a bare strip. */
  label?: ReactNode;
  /** Accessible name for the group when `label` is absent or non-text. */
  ariaLabel?: string;
  /** Dynamic description rendered under the switcher (e.g. the selected option's effect). */
  caption?: ReactNode;
}) {
  const groupName = `${name}-${useId()}`;
  return (
    <fieldset className="brutalist-rgroup" aria-label={ariaLabel}>
      {label !== undefined && <legend className="brutalist-rgroup-legend">{label}</legend>}
      <div className="brutalist-rgroup-seg">
        {options.map((opt) => {
          const checked = opt.value === value;
          return (
            <label
              key={opt.value}
              title={opt.title}
              className={`brutalist-rgroup-opt${checked ? " is-sel" : ""}${opt.disabled ? " is-disabled" : ""}`}
            >
              <input
                type="radio"
                className="brutalist-rgroup-input"
                name={groupName}
                aria-label={opt.ariaLabel ?? opt.value}
                checked={checked}
                disabled={opt.disabled}
                onChange={() => onChange(opt.value)}
              />
              <span className="brutalist-rgroup-face">{opt.label}</span>
            </label>
          );
        })}
      </div>
      {caption !== undefined && <p className="brutalist-rgroup-caption">{caption}</p>}
    </fieldset>
  );
}
